/**
 * MO-01/02 ads through @mp/adapters. Rewarded views are server-proven: POST
 * /v1/ads/reward/start issues a nonce under the slot's daily cap, the provider shows the ad,
 * and /complete marks the nonce earned. dailySpin, fastRestock, freeGems and booster pay out at completion;
 * rescue and doubleTips nonces ride along in the round body and doubleOffline in the offline
 * collect op. Interstitials follow the adapters' InterstitialScheduler and never show to payers.
 *
 * AdMob (the native apps) proves a view with Google's server-side verification callback, which
 * often lands a few seconds after the ad closed. Until it has, /complete answers
 * `409 ad_not_verified`, and nothing is granted then or later by itself: the API only marks the
 * view verified, and the grant happens on the next /complete (within the nonce's hour). So
 * watch() asks again with backoff for about 10 s. After that, a slot that pays out at
 * completion (dailySpin, fastRestock, freeGems, booster) is kept as pending and completed later (settlePending:
 * before the next ad, on return to the foreground and on the app's sync tick), and the player
 * is told the reward is on its way; a slot whose reward belongs to the moment (rescue, double
 * tips, double offline) cannot wait for the callback, so it fails as `unverified`.
 */
import {
  InterstitialScheduler,
  RewardedCaps,
  rewardedOffer,
  type AdSlot,
  type AdsProvider,
  type InterstitialRules,
  type RewardedOffer,
  type RewardedResult,
} from '@mp/adapters/ads';
import { economy } from '../config/content.js';
import type { Analytics } from './analytics.js';
import { ROUTES, errorCode, isOffline, type ApiClient } from './api.js';
import type { AudioAdapter } from './audio.js';
import type { AdCompleteDto, AdStartDto, Reward } from './dto.js';
import type { KV } from './storage.js';
import type { PlayerStore } from './store.js';
import type { SyncService } from './sync.js';
import { localDayKey } from './time.js';

export type { AdSlot } from '@mp/adapters/ads';

/**
 * `pending`: watched, the network has not confirmed it yet, and the reward follows by itself
 * (settlePending); `unverified`: watched, not confirmed in time, and the moment it was for is gone.
 * The service tells the player about both through `setNotice` (callers need not).
 */
export type AdFailure = 'offline' | 'disabled' | 'cap' | 'nofill' | 'failed' | 'dismissed' | 'error' | 'pending' | 'unverified';
export type AdOutcome = { ok: true; nonce: string; granted: Reward } | { ok: false; reason: AdFailure };
/** What the player is told outside the button that started the ad: `pending`, then `arrived`; or `unverified`. */
export type AdNotice = 'pending' | 'arrived' | 'unverified';

/** The toast a rewarded button shows for a failure; null when the service already told the player (setNotice). */
export function adFailureToast(reason: AdFailure): 'ad.dismissed' | 'ad.unavailable' | null {
  return reason === 'pending' || reason === 'unverified' ? null : reason === 'dismissed' ? 'ad.dismissed' : 'ad.unavailable';
}

/** Waits between /complete attempts while the network's callback is on its way (about 10 s in all). */
export const VERIFY_RETRY_MS = [1000, 1500, 2000, 2500, 3000] as const;
/** Slots whose reward the API grants at completion, so a late verification still pays out. */
const PAYS_AT_COMPLETE: ReadonlySet<AdSlot> = new Set<AdSlot>(['dailySpin', 'fastRestock', 'freeGems', 'booster']);
/** The API's nonce lifetime (ads.nonceTtlMs): a pending view older than this can no longer complete. */
export const PENDING_MAX_AGE_MS = 55 * 60_000;
const PENDING_KEY = 'ads.pending';
interface PendingAd {
  slot: AdSlot;
  nonce: string;
  network: string;
  at: number;
}

export interface AdsDeps {
  api: ApiClient;
  provider: AdsProvider;
  kv: KV;
  store: PlayerStore;
  sync: SyncService;
  analytics: Analytics;
  now: () => number;
  /** remote-config flags (ads, interstitials, store) */
  flags: () => Record<string, boolean>;
  /** remote-config interstitial rules over economy.ads.interstitial (any subset); read at every round end */
  interstitialRules?: () => Partial<InterstitialRules> | undefined;
  /** asked before the first ad when no birth year is known (SY-02.1) */
  beforeFirstAd?: () => Promise<void>;
  /** wall-clock wait (H5 minimum watch time, verification retries); tests pass a fake */
  sleep?: (ms: number) => Promise<void>;
  /** tell the player about a reward that is on its way, arrived late, or could not be confirmed */
  notice?: (n: AdNotice) => void;
  /** game sound, held while an ad is up (reference counted, so the H5 SDK's own hooks may overlap) */
  audio?: Pick<AudioAdapter, 'pauseForAd' | 'resumeAfterAd'>;
  /** a visible "loading ad" veil for the whole provider call: loading an ad can take seconds */
  loading?: (on: boolean) => void;
}

export class AdsService {
  readonly caps: RewardedCaps;
  private scheduler: InterstitialScheduler;
  private initialised: Promise<void> | null = null;
  /** why the last round did or did not get an interstitial (tests, analytics) */
  lastDecision: string | null = null;

  constructor(private d: AdsDeps) {
    this.caps = new RewardedCaps(economy.ads.rewarded, d.kv, localDayKey);
    this.scheduler = new InterstitialScheduler(economy.ads.interstitial, d.store.state.interstitial);
  }

  get network(): string {
    return this.d.provider.network;
  }

  /** SY-02.1: ask the age question before the first ad (set by the app once the UI exists). */
  setBeforeFirstAd(fn: () => Promise<void>): void {
    this.d.beforeFirstAd = fn;
  }

  /** The "loading ad" veil (set by the app once the UI exists). */
  setLoading(fn: (on: boolean) => void): void {
    this.d.loading = fn;
  }

  /** Where AdNotice messages go (set by the app once the UI exists). */
  setNotice(fn: (n: AdNotice) => void): void {
    this.d.notice = fn;
  }

  /** Consent/COPPA-aware provider init (re-run after a consent change). */
  init(personalized: boolean, childDirected: boolean): Promise<void> {
    this.initialised = this.d.provider.init({ personalized, childDirected }).catch(() => undefined);
    return this.initialised;
  }

  /** Ads can be requested at all: flag on, API reachable. */
  get enabled(): boolean {
    return this.d.api.enabled && this.d.flags().ads !== false && this.d.sync.status !== 'offline';
  }

  /** A rewarded button can promise an ad right now (MO-01.2 caps + provider fill). */
  async ready(slot: AdSlot): Promise<boolean> {
    if (!this.enabled || !this.caps.canWatch(slot, this.d.now())) return false;
    return this.d.provider.isRewardedReady(slot).catch(() => false);
  }

  /** Synchronous best guess for first render (the button re-checks when pressed). */
  likely(slot: AdSlot): boolean {
    return this.enabled && this.caps.canWatch(slot, this.d.now());
  }

  /** What a rewarded button offers: the ad, or the gem alternative / "try again soon". */
  async offer(slot: AdSlot, gemPrice: number | null): Promise<RewardedOffer> {
    const s = this.d.store.state;
    return rewardedOffer({
      adReady: await this.ready(slot),
      remaining: this.caps.remaining(slot, this.d.now()),
      gemPrice,
      gemBalance: s.gems,
      storeOpen: this.d.flags().store !== false && (!s.coppa || s.parentVerified),
    });
  }

  /** Show one rewarded ad for `slot`; on success the nonce is earned (and spent, for dailySpin / fastRestock). */
  async watch(slot: AdSlot, ref?: string): Promise<AdOutcome> {
    if (!this.enabled) return { ok: false, reason: this.d.api.enabled ? 'offline' : 'disabled' };
    await this.d.beforeFirstAd?.();
    await this.initialised;
    const now = this.d.now;
    void this.settlePending();
    let start: AdStartDto;
    try {
      start = await this.d.api.call<AdStartDto>('POST', ROUTES.adStart, { slot, ...(ref ? { ref } : {}) });
    } catch (e) {
      return { ok: false, reason: this.failure(slot, e) };
    }
    const startedAt = now(); // the server's watch clock started no later than this
    this.caps.syncRemaining(slot, start.remainingToday, now());
    this.d.analytics.track('ad_impression', { slot, network: this.d.provider.network, personalized: start.personalized });
    // the server's per-view consent answer (e.g. set on another device) decides personalisation (AdMob npa)
    const res: RewardedResult = await this.showing(() => this.d.provider.showRewarded(slot, start.nonce, { personalized: start.personalized }))
      .catch(() => ({ completed: false, network: this.d.provider.network, error: 'failed' as const }));
    this.scheduler.onRewardedShown(now());
    this.persist();
    if (!res.completed) {
      this.d.analytics.track('ad_failed', { slot, network: res.network, error: res.error ?? 'failed' });
      return { ok: false, reason: res.error ?? 'failed' };
    }
    // H5 web ads have no network callback: the server's signed proof goes back, never sooner than minWatchMs
    const proof = start.proof ?? res.proof;
    const early = start.proof && start.minWatchMs ? startedAt + start.minWatchMs - now() : 0;
    if (early > 0) await this.sleep(early + 50);
    const body = { nonce: start.nonce, network: res.network, ...(proof ? { proof } : {}) };
    let done: AdCompleteDto | null = null;
    for (let attempt = 0; ; attempt++) {
      try {
        done = await this.d.api.call<AdCompleteDto>('POST', ROUTES.adComplete, body);
        break;
      } catch (e) {
        // AdMob's verification callback has not reached the API yet: ask again shortly
        const wait = errorCode(e) === 'ad_not_verified' ? VERIFY_RETRY_MS[attempt] : undefined;
        if (wait !== undefined) {
          await this.sleep(wait);
          continue;
        }
        if (errorCode(e) !== 'ad_not_verified') return { ok: false, reason: this.failure(slot, e) };
        break;
      }
    }
    if (!done) {
      const pays = PAYS_AT_COMPLETE.has(slot);
      this.d.analytics.track('ad_failed', { slot, network: res.network, error: pays ? 'pending' : 'unverified' });
      if (pays) this.writePending([...this.readPending(), { slot, nonce: start.nonce, network: res.network, at: startedAt }]);
      this.d.notice?.(pays ? 'pending' : 'unverified');
      return { ok: false, reason: pays ? 'pending' : 'unverified' };
    }
    this.caps.record(slot, now());
    this.d.sync.adopt(done.snapshot as never);
    return { ok: true, nonce: start.nonce, granted: done.granted };
  }

  private settling: Promise<number> | null = null;

  /**
   * Complete the views the network confirmed after watch() stopped waiting (dailySpin,
   * fastRestock): each one granted adopts its snapshot and tells the player. Views the API
   * refuses for good (used, expired, unknown, over the cap) are dropped; unconfirmed or
   * offline ones wait for the next call. Resolves to how many were granted; never rejects.
   */
  settlePending(): Promise<number> {
    if (this.settling) return this.settling;
    const list = this.readPending();
    if (!list.length || !this.d.api.enabled) return Promise.resolve(0);
    this.settling = (async () => {
      let granted = 0;
      const keep: PendingAd[] = [];
      for (const p of list) {
        if (this.d.now() - p.at > PENDING_MAX_AGE_MS) continue;
        try {
          const done = await this.d.api.call<AdCompleteDto>('POST', ROUTES.adComplete, { nonce: p.nonce, network: p.network });
          this.caps.record(p.slot, this.d.now());
          this.d.sync.adopt(done.snapshot as never);
          granted++;
        } catch (e) {
          if (errorCode(e) === 'ad_not_verified' || isOffline(e)) keep.push(p);
        }
      }
      // views added while this ran stay
      const added = this.readPending().filter((p) => !list.some((x) => x.nonce === p.nonce));
      this.writePending([...keep, ...added]);
      if (granted) this.d.notice?.('arrived');
      return granted;
    })()
      .catch(() => 0)
      .finally(() => (this.settling = null));
    return this.settling;
  }

  private readPending(): PendingAd[] {
    try {
      const v = JSON.parse(this.d.kv.get(PENDING_KEY) ?? '[]') as unknown;
      return Array.isArray(v) ? (v as PendingAd[]).filter((p) => typeof p?.nonce === 'string' && typeof p.slot === 'string') : [];
    } catch {
      return [];
    }
  }

  private writePending(list: PendingAd[]): void {
    if (list.length) this.d.kv.set(PENDING_KEY, JSON.stringify(list));
    else this.d.kv.remove(PENDING_KEY);
  }

  private sleep(ms: number): Promise<void> {
    return (this.d.sleep ?? ((t: number) => new Promise<void>((r) => setTimeout(r, t))))(ms);
  }

  private failure(slot: AdSlot, e: unknown): AdFailure {
    if (isOffline(e)) return 'offline';
    const code = errorCode(e);
    if (code === 'ad_cap') {
      this.caps.syncRemaining(slot, 0, this.d.now());
      return 'cap';
    }
    if (code === 'flag_off' || code === 'switched_off' || code === 'feature_disabled') return 'disabled';
    return 'error';
  }

  /**
   * MO-02 after every finished round: maybe an interstitial. Never for payers, never below
   * the minimum level, never after a fail, spaced by rounds and time, not right after a
   * rewarded ad (adapters' rules), and only while online with the flags on.
   */
  async afterRound(level: number, result: 'win' | 'fail'): Promise<boolean> {
    const s = this.d.store.state;
    const f = this.d.flags();
    const rules: InterstitialRules = { ...economy.ads.interstitial, ...this.d.interstitialRules?.() };
    const decision = this.scheduler.onRoundEnd({ now: this.d.now(), level, result, payer: s.payer, enabled: this.enabled && f.interstitials !== false }, rules);
    this.lastDecision = decision.reason;
    this.persist();
    if (!decision.show) return false;
    await this.initialised;
    const shown = await this.showing(() => this.d.provider.showInterstitial()).catch(() => ({ shown: false }));
    if (shown.shown) this.scheduler.onInterstitialShown(this.d.now());
    this.persist();
    if (shown.shown) this.d.analytics.track('ad_impression', { slot: 'interstitial', level, network: this.d.provider.network });
    void this.d.api.call('POST', ROUTES.interstitialLog, { afterLevel: level, network: this.d.provider.network, shown: shown.shown }).catch(() => undefined);
    return shown.shown;
  }

  /**
   * Game sound is held for the whole provider call and comes back however it ends (shown,
   * dismissed, no fill, thrown or rejected): music over a web ad breaks the H5 Games Ads policy,
   * and on Android the WebView's timers stall behind AdMob while the audio clock runs on.
   */
  private async showing<T>(show: () => Promise<T>): Promise<T> {
    this.d.audio?.pauseForAd();
    this.d.loading?.(true);
    try {
      return await show();
    } finally {
      this.d.loading?.(false);
      this.d.audio?.resumeAfterAd();
    }
  }

  private persist(): void {
    const st = this.scheduler.state;
    this.d.store.update((s) => (s.interstitial = st));
  }
}
